// packages/core/src/learning/schools/life-design.ts
// ─────────────────────────────────────────────────────────────────────────
// Authored exhibit for "Life design". Matches boglehead-indexing.ts gold
// standard.
//
// ID-bound fields (the validator + compiler guard these):
//   - figures:        FigureId[]  — held [] for the post-authoring cross-ref
//                     pass. Worldview names: vicki-robin, paula-pant,
//                     tanja-hester, ramit-sethi.
//   - applyActions:   ApplyAction union — type-checked at compile time. Only
//                     live/allowed action types emitted.
//   - featuredLessonIds / debateIds — [] until the cross-ref pass.
// ─────────────────────────────────────────────────────────────────────────

import type { SchoolExhibitEntry } from '../SchoolExhibitMeta';

export const LIFE_DESIGN_EXHIBIT: SchoolExhibitEntry = {
	id: 'life-design',
	tagline: 'Life design believes money should shape better days.',
	worldviewSummary:
		'Life design is paying attention to how money choices shape your actual calendar, location, work, relationships, energy, and pace. This lens protects you from chasing financial goals that look impressive but do not improve the life you live. It accepts a real tradeoff: designing your life means making clearer choices, which can require saying no to paths that look normal or prestigious. The blind spot is that life design can become vague lifestyle dreaming if it is not tested against real numbers, constraints, and responsibilities. At its best, this lens turns money into a tool for building days you would actually want to repeat.',
	coreBeliefs: [
		'Life design believes money should serve the life you are trying to build.',
		'Life design believes your calendar reveals your real values faster than your goals do.',
		'Life design believes freedom is not only a number. It is how your days feel.',
		'Life design believes location, work, housing, relationships, and pace are money decisions too.',
		'Life design believes tradeoffs should be chosen on purpose, not inherited from default culture.',
		'Life design believes a better life needs real numbers, not just mood boards.',
	],
	warnsAgainst: [
		'Building wealth without asking what kind of life it is for.',
		'Copying a normal path that does not fit your energy, values, or responsibilities.',
		'Using lifestyle dreams to avoid hard math.',
		'Confusing freedom with constant escape.',
	],
	helps: 'This lens helps people who want money to support a more intentional life, not just a bigger account balance. It may not fit when the user needs immediate stability first, or when life design turns into avoidance instead of a real plan.',
	figures: [],
	canon: [
		{
			title: 'Designing Your Life',
			author: 'Bill Burnett and Dave Evans',
			oneLineIdea:
				'Treat life as something you can test, adjust, and build instead of passively inherit.',
		},
		{
			title: 'Your Money or Your Life',
			author: 'Vicki Robin',
			oneLineIdea:
				'Money decisions should be measured against life energy and the life they support.',
		},
		{
			title: 'Afford Anything',
			author: 'Paula Pant',
			oneLineIdea:
				'You can afford many things, but not everything, so choice is the real discipline.',
		},
		{
			title: 'Work Optional',
			author: 'Tanja Hester',
			oneLineIdea:
				'Financial independence is most useful when it is connected to the life you want after work pressure drops.',
		},
		{
			title: 'Rich Life',
			author: 'Ramit Sethi',
			oneLineIdea:
				'Money works best when it protects what matters to you instead of copying someone else’s values.',
		},
	],
	featuredLessonIds: [],
	nativeModuleId: 'school-life-design',
	debateIds: [],
	applyActions: [
		{
			label: 'Build a freedom plan around the life you want',
			action: {
				type: 'buildFreedomPlan',
				label: 'Build your freedom plan',
			},
		},
		{
			label: 'Check whether your current money picture supports your real life',
			action: {
				type: 'openDashboard',
				label: 'Open your Dashboard',
			},
		},
		{
			label: 'Test how a lower cost life changes your options',
			action: {
				type: 'openSimulator',
				label: 'Simulate a different life setup',
				scenarioType: 'custom',
			},
		},
		{
			label: 'Write the life your money is supposed to support',
			action: {
				type: 'noToolAction',
				label: 'Define the life',
				prompt: 'Write what a good ordinary week would look like if your money supported your actual values.',
			},
		},
	],
	marketingHooks: [
		{
			line: 'Life design asks a brutal question: what is your money actually building?',
			kind: 'hook',
		},
		{
			line: 'The hidden cost of normal success is living a life you never chose.',
			kind: 'hook',
		},
		{
			line: 'What would your ideal ordinary Tuesday look like if money supported it?',
			kind: 'interview-question',
		},
		{
			line: 'Ask someone what they optimized for, then ask what it cost their daily life.',
			kind: 'creator-prompt',
		},
		{
			line: 'Nisk helps users learn the life design lens, then test the tradeoff on their own numbers.',
			kind: 'kickstarter-beat',
		},
		{
			line: 'Show your audience why the best money goal is not always the biggest number.',
			kind: 'creator-prompt',
		},
		{
			line: 'Where does life design help, and where does it become fantasy without math?',
			kind: 'interview-question',
		},
		{
			line: 'A bigger paycheck does not matter if your days still feel trapped.',
			kind: 'hook',
		},
	],
	revision: 1,
};
